// ファイルアップロードページ

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFileUpload } from '../hooks/useFileUpload';
import {
  uploadFiles,
  extractCompanyInfoFromUrl,
  type ExtractFromUrlResponse,
} from '../services/api';
import DropZone from '../components/DropZone';
import FileList from '../components/FileList';
import Alert from '../components/Alert';
import ProgressBar from '../components/ProgressBar';
import BCPStepIndicator from '../components/BCPStepIndicator';
import { createApiRequest } from '../utils/apiResponse';
import { useAppNavigation } from '../utils/navigation';
import {
  buildCompanyInfoFromUpload,
  mergeCompanyInfo,
} from '../utils/companyInfoMapper';
import { logger } from '../utils/logger';
import type { UploadData } from '../types/bcp-generation';

const FileUploadPage: React.FC = () => {
  const navigation = useAppNavigation();
  const location = useLocation();
  const bcpType = location.state?.bcpType || 'simple'; // デフォルトは簡易版
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [companyUrl, setCompanyUrl] = useState('');
  const [isExtractingUrl, setIsExtractingUrl] = useState(false);

  const {
    uploadedFiles,
    isDragging,
    errors,
    addFiles,
    removeFile,
    handleDragEvents,
  } = useFileUpload({
    onUploadComplete: (files) => {
      console.log('Upload completed:', files);
      // BCPページに遷移
      navigation.navigateToBCPCreate({
        uploadedFiles: files,
        bcpType: bcpType,
        delay: 1500,
      });
    },
    onError: (errors) => {
      console.error('Upload errors:', errors);
    },
  });

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    const files = uploadedFiles.map((f) => f.file);

    await createApiRequest({
      request: () =>
        uploadFiles(files, (progressEvent) => {
          setUploadProgress(progressEvent.percentage);
        }),
      onSuccess: (uploadData: UploadData & { all_upload_results?: unknown[] }) => {

        logger.info('Files uploaded successfully', {
          component: 'FileUploadPage',
          action: 'handleUpload',
          data: {
            filesCount: files.length,
            hasExtractedInfo: !!uploadData.extracted_info,
          },
        });

        if (uploadData.session_id) {
          sessionStorage.setItem('upload_session_id', uploadData.session_id);
        }

        // ファイルから会社情報を生成
        const companyInfo = buildCompanyInfoFromUpload(uploadData);

        logger.debug('Company info built from upload', {
          component: 'FileUploadPage',
          action: 'handleUpload',
          data: { companyInfo },
        });

        // 会社情報編集ページに遷移
        navigation.navigateToCompanyInfo({
          companyInfo,
          uploadedFiles,
          bcpType,
          apiResponse: uploadData,
          allUploadResults: uploadData.all_upload_results,
        });
      },
      onError: (error) => {
        logger.error('Upload failed', {
          component: 'FileUploadPage',
          action: 'handleUpload',
          error: error.message,
        });
        // エラー処理は useFileUpload フックで処理される
      },
      onFinally: () => {
        setIsUploading(false);
        setUploadProgress(0);
      },
    });
  };

  const handleFileSelect = (files: File[]) => {
    addFiles(files);
  };

  const handleUrlExtraction = async (
    uploadData?: UploadData & { all_upload_results?: unknown[] }
  ) => {
    if (!companyUrl.trim()) {
      return;
    }

    setIsExtractingUrl(true);

    await createApiRequest({
      request: () => extractCompanyInfoFromUrl(companyUrl.trim()),
      onSuccess: (
        data: NonNullable<ExtractFromUrlResponse['data']> | ExtractFromUrlResponse
      ) => {
        // createApiRequestはhandleApiResponseを通すため、response.dataを返す
        // しかし、型安全のため両方のケースをハンドリング
        let extractedData: NonNullable<ExtractFromUrlResponse['data']>;

        if ('data' in data && data.data) {
          // レスポンス全体が返ってきた場合
          extractedData = data.data;
        } else if ('company_name' in data) {
          // data部分のみが返ってきた場合（正常ケース）
          extractedData = data as NonNullable<ExtractFromUrlResponse['data']>;
        } else {
          logger.warn('No data in URL extraction response', {
            component: 'FileUploadPage',
            action: 'handleUrlExtraction',
            data: { receivedData: data },
          });
          return;
        }

        logger.info('Company info extracted from URL', {
          component: 'FileUploadPage',
          action: 'handleUrlExtraction',
          data: {
            hasCompanyName: !!extractedData.company_name,
          },
        });

        // URL抽出結果から会社情報を構築
        const urlCompanyInfo = {
          companyName: extractedData.company_name || '',
          location: extractedData.location || '',
          employeeCount: extractedData.employee_count || undefined,
          businessDescription: extractedData.business_description || '',
          criticalBusiness: '',
        };

        // uploadDataがある場合はマージ
        let finalCompanyInfo: typeof urlCompanyInfo = urlCompanyInfo;
        if (uploadData) {
          const uploadCompanyInfo = buildCompanyInfoFromUpload(uploadData);
          finalCompanyInfo = mergeCompanyInfo(urlCompanyInfo, uploadCompanyInfo);

          logger.debug('Merged company info from URL and upload', {
            component: 'FileUploadPage',
            action: 'handleUrlExtraction',
            data: { finalCompanyInfo },
          });
        }

        // CompanyInfoPageに遷移
        navigation.navigateToCompanyInfo({
          companyInfo: finalCompanyInfo,
          uploadedFiles,
          bcpType,
          apiResponse: uploadData,
          allUploadResults: uploadData?.all_upload_results,
        });
      },
      onError: (error) => {
        logger.warn('URL extraction failed', {
          component: 'FileUploadPage',
          action: 'handleUrlExtraction',
          error: error.message,
        });

        // URL抽出失敗でもuploadDataがあればファイル抽出結果のみで遷移
        if (uploadData) {
          const companyInfo = buildCompanyInfoFromUpload(uploadData);
          navigation.navigateToCompanyInfo({
            companyInfo,
            uploadedFiles,
            bcpType,
            apiResponse: uploadData,
            allUploadResults: uploadData.all_upload_results,
          });
        }
      },
      onFinally: () => {
        setIsExtractingUrl(false);
      },
    });
  };

  const handleNext = async () => {
    // URL+ファイルの場合: 先にファイルをアップロード → URL抽出 → CompanyInfoPageへ遷移
    if (companyUrl.trim() && uploadedFiles.length > 0) {
      setIsUploading(true);
      setUploadProgress(0);

      const files = uploadedFiles.map((f) => f.file);

      await createApiRequest({
        request: () =>
          uploadFiles(files, (progressEvent) => {
            setUploadProgress(progressEvent.percentage);
          }),
        onSuccess: async (
          uploadData: UploadData & { all_upload_results?: unknown[] }
        ) => {

          logger.info('Files uploaded, starting URL extraction', {
            component: 'FileUploadPage',
            action: 'handleNext',
          });

          if (uploadData.session_id) {
            sessionStorage.setItem('upload_session_id', uploadData.session_id);
          }

          // ファイルアップロード後にURL抽出
          await handleUrlExtraction(uploadData);
        },
        onError: (error) => {
          logger.error('Upload failed in handleNext', {
            component: 'FileUploadPage',
            action: 'handleNext',
            error: error.message,
          });
        },
        onFinally: () => {
          setIsUploading(false);
          setUploadProgress(0);
        },
      });
      return;
    }

    // URLのみの場合: URL抽出 → CompanyInfoPageへ遷移
    if (companyUrl.trim()) {
      await handleUrlExtraction();
      return;
    }

    // ファイルのみの場合: ファイルアップロード → CompanyInfoPageへ遷移
    if (uploadedFiles.length > 0) {
      await handleUpload();
      return;
    }

    // 両方がない場合: 空の companyInfo で /company-info へ遷移
    logger.info('No input provided, navigating to CompanyInfoPage', {
      component: 'FileUploadPage',
      action: 'handleNext',
    });

    navigation.navigateToCompanyInfo({
      companyInfo: {
        companyName: '',
        location: '',
        employeeCount: undefined,
        businessDescription: '',
        criticalBusiness: '',
      },
      uploadedFiles: [],
      bcpType,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* ステップインジケーター */}
        <BCPStepIndicator currentStep={1} />

        {/* ページタイトル */}
        <div className="mb-8 mt-8">
          <h2 className="text-2xl font-bold text-[#0d357f] mb-3">企業情報登録</h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            BCP策定に必要な会社の基本情報を登録します。<br />
            会社概要が分かるホームページがある場合はURLを入力してください。また、会社概要等の資料をアップロードしてください。<br />
            これらの情報をもとに、貴社の事業内容に応じたBCPの下書きを自動生成します。
          </p>
          <p className="text-sm text-red-600 font-semibold mt-3">
            ホームページやアップロード可能な資料がない場合は、次へお進みください。
          </p>
        </div>

        {/* メインコンテンツ */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* 会社概要ページURL入力 */}
          <div className="mb-8">
            <label className="block text-sm font-semibold mb-3 text-[#0d357f]">
              会社概要ページURL
            </label>
            <input
              type="url"
              placeholder="https://example.com/company/"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              disabled={isUploading || isExtractingUrl}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              貴社の公式ウェブサイトの会社概要ページのURLを入力してください(例: https://example.com/company/)
            </p>
          </div>

          {/* ファイルアップロード */}
          <div className="mb-8">
            <label className="block text-sm font-semibold mb-4 text-[#0d357f]">
              ファイルアップロード
            </label>

            {/* エラー表示 */}
            {errors.length > 0 && (
              <Alert
                type="error"
                title="アップロードエラー"
                messages={errors.map(error => error.message)}
                className="mb-4"
              />
            )}

            {/* ドラッグ&ドロップエリア */}
            <DropZone
              isDragging={isDragging}
              onFileSelect={handleFileSelect}
              disabled={isUploading}
              {...handleDragEvents}
            />

            {/* ファイルリスト */}
            {uploadedFiles.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">アップロード済みファイル一覧</h4>
                <FileList
                  files={uploadedFiles}
                  onRemove={removeFile}
                  showProgress={isUploading}
                />
                {/* アップロード進捗 */}
                {isUploading && (
                  <ProgressBar
                    value={uploadProgress}
                    className="mt-4"
                    animate
                  />
                )}
              </div>
            )}

            {/* アップロード情報 */}
            <div className="mt-6 bg-[#e8f4fd] border border-[#b3d9f2] rounded-lg p-5">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#0d357f] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm">
                  <p className="font-semibold text-[#0d357f] mb-2">アップロード推奨ファイル</p>
                  <p className="text-gray-700 mb-2">会社名・所在地・従業員数・事業内容が含まれるファイルをアップロードしてください。</p>
                  <p className="text-gray-700 mb-1.5">例)</p>
                  <ul className="text-gray-700 space-y-1.5">
                    <li>• 会社案内・会社概要資料</li>
                    <li>• 事業計画書・中期経営計画</li>
                    <li>• 組織図・従業員名簿</li>
                    <li>• 財務書表・決算書類</li>
                    <li>• 主要取引先一覧</li>
                    <li>• 設備・資産一覧</li>
                    <li>• 既存の防災・リスク管理資料</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 重要な注記事項 */}
            <div className="mt-4 bg-[#fffbf0] border border-[#f5e6c8] rounded-lg p-5">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900 mb-2">注記事項</p>
                  <ul className="text-gray-700 space-y-1.5">
                    <li>• アップロードされた情報は、BCP策定の目的のみに使用され、目的外使用は一切行いません。また、他社のBCP策定に使用することはありません。</li>
                    <li>• ユーザーがアップロードした会社情報ファイルは、アップロードから1ヶ月後に自動削除されます。</li>
                    <li>• 同一企業に属するユーザー間でも情報を共有することはありません（個人単位での管理）。</li>
                    <li>• システムは厳密なセキュリティ対策を実施しており、機密情報の保護に努めています。</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* アクションボタン */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <button
              onClick={() => window.history.back()}
              disabled={isUploading || isExtractingUrl}
              className="px-6 py-2.5 rounded-lg font-medium text-sm transition-colors border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              戻る
            </button>

            <button
              onClick={handleNext}
              disabled={isUploading || isExtractingUrl}
              className="px-8 py-2.5 rounded-lg font-semibold text-sm transition-colors bg-[#0d357f] hover:bg-[#0a2a64] text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <span>アップロード中... {uploadProgress}%</span>
              ) : isExtractingUrl ? (
                <span>情報を抽出中...</span>
              ) : (
                '次へ進む'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadPage;
